import type { FSCA, SCNode } from "../engines/renderingEngine/fsc.types";
import type { LineProps } from "../engines/coreComponents/types";
import { Line } from "./LineNode";
import { getElementByKey } from "../engines/renderingEngine/renderer";
import type { THEME } from "./colorPalette.constants";
import { styles } from "./scomponents.styles";
import type { FlowLine } from "@certa/studio/src/hooks/processLookup/flowLayoutCalculator";
import {
  getFlowStepKey,
  getFlowSwimlaneKey,
  getSwimlaneIdFromFlowSwimlaneDownwardsRuleIndicatorKey,
  getSwimlaneIdFromFlowSwimlaneUpwardsRuleIndicatorKey
} from "../utils/general.utils";
import { appState } from "../utils/store";
import type { ChangedComponentsByChangeType } from "@certa/studio/src/hooks/useStudioAPI";

export type FlowLineNodeProps = {
  selectedKey: string;
  flowLines: FlowLine[];
  theme: THEME;
  componentsToHighlight: ChangedComponentsByChangeType;
};

export const FlowLineNode: FSCA<FlowLineNodeProps> = ({
  flowLines,
  theme,
  selectedKey,
  componentsToHighlight
}) => {
  if (!flowLines || flowLines.length === 0) {
    return [];
  }
  const hoveredComponentKey = componentsToHighlight.hoveredComponent;
  const shouldShowStepFlowLines: boolean =
    appState.shouldShowStepFlowLines ?? false;

  const scLines: SCNode<LineProps>[] = [];

  flowLines
    .filter(
      flowLine =>
        (!flowLine.isSwimlaneLine && shouldShowStepFlowLines) ||
        flowLine.isSwimlaneLine
    )
    .forEach(flowLine => {
      let sourceKey: string = "";

      let targetKey: string = "";

      if (flowLine.isSwimlaneLine) {
        sourceKey = getFlowSwimlaneKey(flowLine.sourceId);
        targetKey = getFlowSwimlaneKey(flowLine.targetId);
      } else {
        sourceKey = getFlowStepKey(flowLine.sourceId);
        targetKey = getFlowStepKey(flowLine.targetId);
      }
      const key = `FlowLine-${flowLine.id}`;

      const isSelected =
        selectedKey === key ||
        getIsSelectedByRuleIndicator(selectedKey, flowLine);
      const isHovered =
        hoveredComponentKey === key ||
        getIsSelectedByRuleIndicator(hoveredComponentKey, flowLine);
      const sourceComponent = getElementByKey(sourceKey);
      const targetComponent = getElementByKey(targetKey);

      if (sourceComponent && targetComponent) {
        const flowLineType = flowLine.isSwimlaneLine ? "swimlane" : "step";
        const style = getFlowLineStyle({
          theme,
          isSelected,
          isHovered,
          flowLineType,
          hasTransitiveFlowLines: flowLine.transitiveFlowLinesIds.size > 0
        });
        scLines.push(
          Line({
            key,
            sourceNodeId: sourceComponent.props.key,
            targetNodeId: targetComponent.props.key,
            startNodeHash: flowLine.sourceHash,
            endNodeHash: flowLine.targetHash,
            flowLineType,
            isSelected,
            isHovered,
            style
          }) as SCNode<LineProps>
        );
      }
    });

  return scLines;
};

const getFlowLineStyle = ({
  theme,
  isSelected,
  isHovered,
  flowLineType,
  hasTransitiveFlowLines
}: {
  theme: THEME;
  isSelected: boolean;
  isHovered: boolean;
  flowLineType: "swimlane" | "step";
  hasTransitiveFlowLines: boolean;
}): LineProps["style"] => {
  const flowLineStyle = styles[theme].flowLine;
  const flowLineSelectedStyle = styles[theme].flowLineSelected;
  const flowLineStepStyle = styles[theme].flowLineStep;
  let style: LineProps["style"] = {};

  if (flowLineType === "swimlane") {
    style = isSelected || isHovered ? flowLineSelectedStyle : flowLineStyle;
  } else {
    style = flowLineStepStyle;
  }

  style.visible = !hasTransitiveFlowLines || isSelected || isHovered;

  return style;
};

const getIsSelectedByRuleIndicator = (
  selectedKey: string | null,
  flowLine: FlowLine
) => {
  if (!selectedKey) {
    return false;
  }
  const selectedSourceSwimlaneId =
    getSwimlaneIdFromFlowSwimlaneDownwardsRuleIndicatorKey(selectedKey);
  if (selectedSourceSwimlaneId) {
    return flowLine.sourceId === selectedSourceSwimlaneId;
  } else {
    const selectedTargetSwimlaneId =
      getSwimlaneIdFromFlowSwimlaneUpwardsRuleIndicatorKey(selectedKey);
    return flowLine.targetId === selectedTargetSwimlaneId;
  }
};
